import { useEffect, useRef, useState } from 'react';
import { Stepper } from '@braskai/ui-kit';
import { useSettingsStore } from '@/entities/user/settings';
import { API } from '@/shared/api';
import { TELL_ABOUT_YOURSELF_SURVEY_DEFAULT_DATA } from '@/shared/constants-tmp/surveys';
import { useAppDispatch } from '@/shared/hooks-tmp/reduxToolkitHooks';

import { getSurvey } from '@/shared/utils-tmp/getSurvey';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { getSurveyDataForMixpanel } from '@/shared/utils-tmp/getSurveyDataForMixpanel';

import { getUser } from '@/shared/store-tmp/user/thunks/getUser';
import { checkIsUserNeedToPay } from '@/shared/store-tmp/billing/thunks/checkIsUserNeedToPay';
import { showErrorAlert } from '@/shared/utils-tmp/alert/showErrorAlert';
import { useBillingStore } from '@/shared/hooks-tmp/reduxHooks/useBillingStore';
import { Dialog, DialogTitle } from '../../../ui-tmp';
import { Content, StepperContainer } from './styled';
import { FormValues, Survey } from './types';
import { DEFAULT_VALUES, FORMS, SURVEY } from './constants';
import { Question } from './components/Question';
import { ThankYou } from './components/ThankYou';

const CLOSE_MODAL_TIMEOUT = 2000;

type SurveyModalProps = {
  onClose: () => void;
};

export const SurveyModal = ({ onClose }: SurveyModalProps) => {
  const dispatch = useAppDispatch();

  const [activeStep, setActiveStep] = useState(0);
  const [answers, setAnswers] = useState(DEFAULT_VALUES);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isThankYouOpen, setIsThankYouOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setInterval>>();

  const surveys = useSettingsStore((state) => state.surveys);

  const {
    balance: { hasTrialPlan },
  } = useBillingStore();

  const survey = getSurvey(surveys, TELL_ABOUT_YOURSELF_SURVEY_DEFAULT_DATA.id);

  const reward = hasTrialPlan ? survey?.trial_user_survey_reward : survey?.subscribed_user_survey_reward;
  const text = `${reward} minute${reward === 1 ? '' : 's'}`;

  const totalSteps = Object.keys(SURVEY).length;
  const isLastStep = activeStep === totalSteps - 1;

  const handleNext = (input: FormValues) => {
    setAnswers((prevState) => ({
      ...prevState,
      ...input,
    }));
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async (input: FormValues) => {
    try {
      if (!isLastStep) {
        handleNext(input);
        return;
      }

      setIsLoading(true);

      const data = {
        ...TELL_ABOUT_YOURSELF_SURVEY_DEFAULT_DATA,
        survey_data: {
          ...answers,
          ...input,
        },
      };

      const eventData = {
        ...getSurveyDataForMixpanel(answers),
        feedback: input.feedback.text,
      };

      const response = await API.postSurvey(data);

      if (response.ok) {
        await dispatch(getUser({}));
        setIsSubmitted(true);
        setIsThankYouOpen(true);

        await dispatch(checkIsUserNeedToPay());
      }

      MixpanelEvents.bonusSurveyCompleted(eventData);
    } catch (e) {
      showErrorAlert(e);
    }
    setIsLoading(false);
  };

  const renderForm = () => {
    const data = SURVEY[FORMS[activeStep] as keyof Survey];

    const values = answers[FORMS[activeStep] as keyof Survey];
    return (
      <Question
        value={values}
        key={activeStep}
        onBack={handleBack}
        data={data}
        onSubmit={handleSubmit}
        isLastStep={isLastStep}
        isLoading={isLoading}
      />
    );
  };

  useEffect(() => {
    if (isThankYouOpen) {
      timeoutRef.current = setTimeout(() => {
        setIsThankYouOpen(false);
      }, CLOSE_MODAL_TIMEOUT);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [isThankYouOpen]);

  if (isSubmitted && !isThankYouOpen) {
    return null;
  }

  return (
    <Dialog open onClose={isLoading ? undefined : onClose} size="sm">
      {isSubmitted ? (
        <ThankYou text={text} />
      ) : (
        <>
          <DialogTitle
            onClose={onClose}
            buttonsDisabled={isLoading}
            renderTitle={() => (
              <span>
                Tell us about yourself and <br /> get {reward} minute{`${reward === 1 ? '' : 's'}`} for free 🎁
              </span>
            )}
          />

          <Content>{renderForm()}</Content>
          <StepperContainer>
            <Stepper steps={totalSteps} activeStep={activeStep} />
          </StepperContainer>
        </>
      )}
    </Dialog>
  );
};
